import { useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';

const HIGHLIGHT_WORDS = ['meaningful', 'collaborative', 'leadership', 'investing', 'people'];

const PORTFOLIO_CARDS = [
  { rot: -7, top: '2%', left: '0%', w: '46%', z: 30 },
  { rot: 5, top: '22%', left: '44%', w: '44%', z: 20 },
  { rot: -3, top: '46%', left: '-6%', w: '46%', z: 40 },
  { rot: 6, top: '68%', left: '40%', w: '42%', z: 10 },
];

const jobsCtaDef: SlotGroupDefinition = {
  label: 'Jobs CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Open Positions' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cultureCtaDef: SlotGroupDefinition = {
  label: 'Culture CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/culture' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn About Our Culture' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const internshipCtaDef: SlotGroupDefinition = {
  label: 'Internship CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Internship Opportunities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const portfolioCtaDef: SlotGroupDefinition = {
  label: 'Portfolio CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/our-communities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Our Portfolio' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const trademarkDef: SlotGroupDefinition = {
  label: 'Trademark Graphic',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'ALA Trademark' },
  },
};

const culturePhoto1Def: SlotGroupDefinition = {
  label: 'Culture Photo 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Culture photo 1' },
  },
};

const culturePhoto2Def: SlotGroupDefinition = {
  label: 'Culture Photo 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Culture photo 2' },
  },
};

const culturePhoto3Def: SlotGroupDefinition = {
  label: 'Culture Photo 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Culture photo 3' },
  },
};

const culturePhoto4Def: SlotGroupDefinition = {
  label: 'Culture Photo 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Culture photo 4' },
  },
};

const award1Def: SlotGroupDefinition = {
  label: 'Award Logo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Top Workplaces 2026 – jacksonville.com, The Florida Times-Union',
    },
  },
};

const award2Def: SlotGroupDefinition = {
  label: 'Award Logo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Top Workplaces 2025 – Houston Chronicle' },
  },
};

const award3Def: SlotGroupDefinition = {
  label: 'Award Logo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Top Workplaces 2026 – Tampa Bay Times' },
  },
};

const award4Def: SlotGroupDefinition = {
  label: 'Award Logo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'USA Today Top Workplaces USA 2025-2026' },
  },
};

const award5Def: SlotGroupDefinition = {
  label: 'Award Logo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Top Workplaces 2023-2025 – Houston Chronicle',
    },
  },
};

const internshipPhoto1Def: SlotGroupDefinition = {
  label: 'Internship Photo 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Internship photo 1' },
  },
};

const internshipPhoto2Def: SlotGroupDefinition = {
  label: 'Internship Photo 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Internship photo 2' },
  },
};

const portfolioPhoto1Def: SlotGroupDefinition = {
  label: 'Portfolio Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Portfolio community photo 1' },
  },
};

const portfolioPhoto2Def: SlotGroupDefinition = {
  label: 'Portfolio Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Portfolio community photo 2' },
  },
};

const portfolioPhoto3Def: SlotGroupDefinition = {
  label: 'Portfolio Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Portfolio community photo 3' },
  },
};

const portfolioPhoto4Def: SlotGroupDefinition = {
  label: 'Portfolio Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Portfolio community photo 4' },
  },
};

const galleryPhoto1Def: SlotGroupDefinition = {
  label: 'Gallery Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 1' },
  },
};

const galleryPhoto2Def: SlotGroupDefinition = {
  label: 'Gallery Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 2' },
  },
};

const galleryPhoto3Def: SlotGroupDefinition = {
  label: 'Gallery Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 3' },
  },
};

const galleryPhoto4Def: SlotGroupDefinition = {
  label: 'Gallery Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 4' },
  },
};

const galleryPhoto5Def: SlotGroupDefinition = {
  label: 'Gallery Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 5' },
  },
};

const galleryPhoto6Def: SlotGroupDefinition = {
  label: 'Gallery Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 6' },
  },
};

const galleryPhoto7Def: SlotGroupDefinition = {
  label: 'Gallery Photo 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 7' },
  },
};

const galleryPhoto8Def: SlotGroupDefinition = {
  label: 'Gallery Photo 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 8' },
  },
};

const galleryPhoto9Def: SlotGroupDefinition = {
  label: 'Gallery Photo 9',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 9' },
  },
};

const galleryPhoto10Def: SlotGroupDefinition = {
  label: 'Gallery Photo 10',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team culture photo 10' },
  },
};

const primaryCtaClass =
  'group inline-flex w-fit items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]';

const secondaryCtaClass =
  'group inline-flex w-fit items-center justify-center gap-2 rounded-full px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-dark))] shadow-[inset_0_0_0_2px_rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-secondary))]';

function PortfolioCard({ index, image, alt }: { index: number; image: string; alt: string }) {
  const card = PORTFOLIO_CARDS[index];
  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotate: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, rotate: card.rot, scale: 1 }}
      viewport={alaViewport}
      transition={{ delay: 0.2 + index * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{
        top: card.top,
        left: card.left,
        width: card.w,
        zIndex: card.z,
      }}
      className="absolute aspect-[3/4] overflow-clip rounded-xl shadow-2xl ring-1 ring-[rgb(var(--ala-on-primary))]/10"
    >
      <VibesitesImage url={image} alt={alt} sizes="40vw" className="h-full w-full object-cover" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
    </motion.div>
  );
}

function CtaArrow({ light }: { light?: boolean }) {
  return (
    <ArrowRight
      strokeWidth={2}
      className={`h-5 w-5 transition-transform group-hover:translate-x-1 ${
        light ? 'text-[rgb(var(--ala-on-secondary))]' : 'text-[rgb(var(--ala-dark))] group-hover:text-[rgb(var(--ala-on-secondary))]'
      }`}
    />
  );
}

function ScrollWord({
  word,
  range,
  progress,
  isHighlight,
}: {
  word: string;
  range: [number, number];
  progress: MotionValue<number>;
  isHighlight: boolean;
}) {
  const opacity = useTransform(progress, range, [0.12, 1]);
  return (
    <span className="mr-[0.25em] inline-block">
      <motion.span
        style={{ opacity }}
        className={isHighlight ? 'font-medium text-[rgb(var(--ala-secondary))]' : 'font-medium'}
      >
        {word}
      </motion.span>
    </span>
  );
}

function ScrollHighlightIntro({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.85', 'start 0.25'],
  });
  const words = text.split(/\s+/).filter(Boolean);

  return (
    <p
      ref={ref}
      className="relative z-10 mt-24 max-w-[600px] px-6 text-center font-body text-[clamp(24px,2.36vw,34px)] font-normal leading-[1.4] text-[rgb(var(--ala-text))]"
    >
      {words.map((word, i) => {
        const start = i / words.length;
        const end = start + 1 / words.length;
        const normalized = word.toLowerCase().replace(/[.,'’"]/g, '');
        return (
          <ScrollWord
            key={`${word}-${i}`}
            word={word}
            range={[start, end]}
            progress={scrollYProgress}
            isHighlight={HIGHLIGHT_WORDS.includes(normalized)}
          />
        );
      })}
    </p>
  );
}

const MIN_AWARDS_PER_HALF = 8;

function AwardCard({ image, alt }: { image: string; alt: string }) {
  return (
    <div className="flex h-[140px] w-[260px] shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--ala-background))] p-4 shadow-sm">
      {image ? (
        <VibesitesImage url={image} alt={alt} sizes="260px" className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="font-heading text-sm text-[rgb(var(--ala-text-muted))]">Upload award logo</span>
      )}
    </div>
  );
}

function AwardsMarquee() {
  const award1 = useSlotGroup('careerscontent.award_1', award1Def);
  const award2 = useSlotGroup('careerscontent.award_2', award2Def);
  const award3 = useSlotGroup('careerscontent.award_3', award3Def);
  const award4 = useSlotGroup('careerscontent.award_4', award4Def);
  const award5 = useSlotGroup('careerscontent.award_5', award5Def);
  const isEditing = Boolean(useEditMode());

  const awards = [award1, award2, award3, award4, award5].map((award) => ({
    image: award.image || '',
    alt: award.alt || '',
  }));
  const visible = isEditing ? awards : awards.filter((award) => award.image);
  if (visible.length === 0) return null;

  const show = (image: string | null | undefined) => isEditing || Boolean(image);
  const copiesPerHalf = Math.ceil(MIN_AWARDS_PER_HALF / visible.length);
  const fillerCopies = copiesPerHalf * 2 - 1;

  return (
    <div className="w-full overflow-clip py-16">
      <motion.div
        className="flex w-max items-center gap-8"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ repeat: Infinity, ease: 'linear', duration: visible.length * copiesPerHalf * 5 }}
      >
        {show(award1.image) ? (
          <EditableSlotGroup groupPath="careerscontent.award_1" definition={award1Def}>
            <AwardCard image={award1.image || ''} alt={award1.alt || ''} />
          </EditableSlotGroup>
        ) : null}
        {show(award2.image) ? (
          <EditableSlotGroup groupPath="careerscontent.award_2" definition={award2Def}>
            <AwardCard image={award2.image || ''} alt={award2.alt || ''} />
          </EditableSlotGroup>
        ) : null}
        {show(award3.image) ? (
          <EditableSlotGroup groupPath="careerscontent.award_3" definition={award3Def}>
            <AwardCard image={award3.image || ''} alt={award3.alt || ''} />
          </EditableSlotGroup>
        ) : null}
        {show(award4.image) ? (
          <EditableSlotGroup groupPath="careerscontent.award_4" definition={award4Def}>
            <AwardCard image={award4.image || ''} alt={award4.alt || ''} />
          </EditableSlotGroup>
        ) : null}
        {show(award5.image) ? (
          <EditableSlotGroup groupPath="careerscontent.award_5" definition={award5Def}>
            <AwardCard image={award5.image || ''} alt={award5.alt || ''} />
          </EditableSlotGroup>
        ) : null}
        {Array.from({ length: fillerCopies }, (_, copy) =>
          visible.map((award, i) => (
            <div key={`award-copy-${copy}-${i}`} aria-hidden className="shrink-0">
              <AwardCard image={award.image} alt="" />
            </div>
          )),
        )}
      </motion.div>
    </div>
  );
}

const MIN_GALLERY_PER_HALF = 5;
const GALLERY_HEIGHTS_A = ['h-[256px]', 'h-[288px]', 'h-[384px]'];
const GALLERY_HEIGHTS_B = ['h-[288px]', 'h-[384px]', 'h-[256px]'];

type GalleryPhoto = { image: string; alt: string; height: string };

function GalleryTile({ image, alt, height }: GalleryPhoto) {
  return (
    <div className={`w-[384px] shrink-0 overflow-clip rounded-lg ${height}`}>
      {image ? (
        <VibesitesImage url={image} alt={alt} sizes="384px" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[rgb(var(--ala-border))]/60 font-heading text-sm text-[rgb(var(--ala-text-muted))]">
          Upload photo
        </div>
      )}
    </div>
  );
}

function galleryLoop(photos: GalleryPhoto[]) {
  const copiesPerHalf = Math.ceil(MIN_GALLERY_PER_HALF / Math.max(photos.length, 1));
  return { copiesPerHalf, fillerCopies: copiesPerHalf * 2 - 1 };
}

function GalleryFiller({ photos, copies, rowKey }: { photos: GalleryPhoto[]; copies: number; rowKey: string }) {
  return (
    <>
      {Array.from({ length: copies }, (_, copy) =>
        photos.map((photo, i) => (
          <div key={`${rowKey}-copy-${copy}-${i}`} aria-hidden className="shrink-0">
            <GalleryTile image={photo.image} alt="" height={photo.height} />
          </div>
        )),
      )}
    </>
  );
}

function GalleryMarquee() {
  const photo1 = useSlotGroup('careerscontent.gallery_photo_1', galleryPhoto1Def);
  const photo2 = useSlotGroup('careerscontent.gallery_photo_2', galleryPhoto2Def);
  const photo3 = useSlotGroup('careerscontent.gallery_photo_3', galleryPhoto3Def);
  const photo4 = useSlotGroup('careerscontent.gallery_photo_4', galleryPhoto4Def);
  const photo5 = useSlotGroup('careerscontent.gallery_photo_5', galleryPhoto5Def);
  const photo6 = useSlotGroup('careerscontent.gallery_photo_6', galleryPhoto6Def);
  const photo7 = useSlotGroup('careerscontent.gallery_photo_7', galleryPhoto7Def);
  const photo8 = useSlotGroup('careerscontent.gallery_photo_8', galleryPhoto8Def);
  const photo9 = useSlotGroup('careerscontent.gallery_photo_9', galleryPhoto9Def);
  const photo10 = useSlotGroup('careerscontent.gallery_photo_10', galleryPhoto10Def);
  const isEditing = Boolean(useEditMode());

  const toPhotos = (slots: (typeof photo1)[], heights: string[]): GalleryPhoto[] =>
    slots.map((slot, i) => ({ image: slot.image || '', alt: slot.alt || '', height: heights[i % 3] }));
  const rowA = toPhotos([photo1, photo2, photo3, photo4, photo5], GALLERY_HEIGHTS_A);
  const rowB = toPhotos([photo6, photo7, photo8, photo9, photo10], GALLERY_HEIGHTS_B);
  const visibleA = isEditing ? rowA : rowA.filter((photo) => photo.image);
  const visibleB = isEditing ? rowB : rowB.filter((photo) => photo.image);
  if (visibleA.length === 0 && visibleB.length === 0) return null;

  const show = (image: string | null | undefined) => isEditing || Boolean(image);
  const loopA = galleryLoop(visibleA);
  const loopB = galleryLoop(visibleB);

  return (
    <section className="flex w-full flex-col gap-10 overflow-clip bg-[rgb(var(--ala-surface))] py-24">
      {visibleA.length > 0 ? (
        <div className="flex w-[200%]">
          <motion.div
            className="flex shrink-0 items-end gap-10"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ repeat: Infinity, ease: 'linear', duration: visibleA.length * loopA.copiesPerHalf * 6 }}
          >
            {show(photo1.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_1" definition={galleryPhoto1Def}>
                <GalleryTile {...rowA[0]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo2.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_2" definition={galleryPhoto2Def}>
                <GalleryTile {...rowA[1]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo3.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_3" definition={galleryPhoto3Def}>
                <GalleryTile {...rowA[2]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo4.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_4" definition={galleryPhoto4Def}>
                <GalleryTile {...rowA[3]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo5.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_5" definition={galleryPhoto5Def}>
                <GalleryTile {...rowA[4]} />
              </EditableSlotGroup>
            ) : null}
            <GalleryFiller photos={visibleA} copies={loopA.fillerCopies} rowKey="a" />
          </motion.div>
        </div>
      ) : null}
      {visibleB.length > 0 ? (
        <div className="flex w-[200%] justify-end">
          <motion.div
            className="flex shrink-0 items-start gap-10"
            animate={{ x: ['-50%', '0%'] }}
            transition={{ repeat: Infinity, ease: 'linear', duration: visibleB.length * loopB.copiesPerHalf * 7 }}
          >
            {show(photo6.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_6" definition={galleryPhoto6Def}>
                <GalleryTile {...rowB[0]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo7.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_7" definition={galleryPhoto7Def}>
                <GalleryTile {...rowB[1]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo8.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_8" definition={galleryPhoto8Def}>
                <GalleryTile {...rowB[2]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo9.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_9" definition={galleryPhoto9Def}>
                <GalleryTile {...rowB[3]} />
              </EditableSlotGroup>
            ) : null}
            {show(photo10.image) ? (
              <EditableSlotGroup groupPath="careerscontent.gallery_photo_10" definition={galleryPhoto10Def}>
                <GalleryTile {...rowB[4]} />
              </EditableSlotGroup>
            ) : null}
            <GalleryFiller photos={visibleB} copies={loopB.fillerCopies} rowKey="b" />
          </motion.div>
        </div>
      ) : null}
    </section>
  );
}

function QuotesCarousel() {
  const [index, setIndex] = useState(0);
  const total = 3;
  const prev = () => setIndex((current) => (current <= 0 ? total - 1 : current - 1));
  const next = () => setIndex((current) => (current + 1) % total);
  const quoteClass =
    'font-heading text-[clamp(18px,1.72vw,28px)] font-medium leading-[1.4] tracking-[-0.039em] text-[rgb(var(--ala-text))]';

  return (
    <section className="flex w-full justify-center overflow-clip bg-[rgb(var(--ala-surface))] px-4 py-20 md:px-8 md:py-32">
      <div className="flex w-full max-w-[1380px] flex-col gap-12">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="careerscontent.quotes_eyebrow"
              label="Quotes Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-text-muted))]"
            />
          </div>
          <RichTextContent
            path="careerscontent.quotes_headline"
            label="Quotes Headline"
            as="h2"
            className="font-heading text-[clamp(36px,4.2vw,60px)] font-medium leading-[1.1] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          />
        </div>

        <div className="relative border-t border-[rgb(var(--ala-accent))] pt-12">
          <div className="relative min-h-[8rem] pr-0 md:min-h-[10rem] md:pr-32">
            <motion.div
              initial={false}
              animate={{ opacity: index === 0 ? 1 : 0, x: index === 0 ? 0 : -24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 0 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 0}
            >
              <RichTextContent path="careerscontent.quote_1" label="Quote 1" as="p" className={quoteClass} />
            </motion.div>
            <motion.div
              initial={false}
              animate={{
                opacity: index === 1 ? 1 : 0,
                x: index === 1 ? 0 : index < 1 ? -24 : 24,
              }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 1 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 1}
            >
              <RichTextContent path="careerscontent.quote_2" label="Quote 2" as="p" className={quoteClass} />
            </motion.div>
            <motion.div
              initial={false}
              animate={{ opacity: index === 2 ? 1 : 0, x: index === 2 ? 0 : 24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 2 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 2}
            >
              <RichTextContent path="careerscontent.quote_3" label="Quote 3" as="p" className={quoteClass} />
            </motion.div>
          </div>

          <div className="relative mt-6 flex justify-end gap-4 md:absolute md:right-0 md:top-6 md:mt-0">
            <button
              type="button"
              aria-label="Previous quote"
              onClick={prev}
              className="flex h-[52px] w-[60px] items-center justify-center rounded bg-[rgb(var(--ala-border))]/60 transition-colors hover:bg-[rgb(var(--ala-border))]"
            >
              <ArrowLeft className="h-4 w-4 text-[rgb(var(--ala-text-muted))]" />
            </button>
            <button
              type="button"
              aria-label="Next quote"
              onClick={next}
              className="flex h-[52px] w-[60px] items-center justify-center rounded bg-[rgb(var(--ala-border))]/60 transition-colors hover:bg-[rgb(var(--ala-border))]"
            >
              <ArrowRight className="h-4 w-4 text-[rgb(var(--ala-text-muted))]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CareersContent() {
  const jobsCta = useSlotGroup('careerscontent.jobs_cta', jobsCtaDef);
  const cultureCta = useSlotGroup('careerscontent.culture_cta', cultureCtaDef);
  const internshipCta = useSlotGroup('careerscontent.internship_cta', internshipCtaDef);
  const portfolioCta = useSlotGroup('careerscontent.portfolio_cta', portfolioCtaDef);
  const trademark = useSlotGroup('careerscontent.trademark', trademarkDef);
  const culture1 = useSlotGroup('careerscontent.culture_photo_1', culturePhoto1Def);
  const culture2 = useSlotGroup('careerscontent.culture_photo_2', culturePhoto2Def);
  const culture3 = useSlotGroup('careerscontent.culture_photo_3', culturePhoto3Def);
  const culture4 = useSlotGroup('careerscontent.culture_photo_4', culturePhoto4Def);
  const internship1 = useSlotGroup('careerscontent.internship_photo_1', internshipPhoto1Def);
  const internship2 = useSlotGroup('careerscontent.internship_photo_2', internshipPhoto2Def);
  const portfolio1 = useSlotGroup('careerscontent.portfolio_photo_1', portfolioPhoto1Def);
  const portfolio2 = useSlotGroup('careerscontent.portfolio_photo_2', portfolioPhoto2Def);
  const portfolio3 = useSlotGroup('careerscontent.portfolio_photo_3', portfolioPhoto3Def);
  const portfolio4 = useSlotGroup('careerscontent.portfolio_photo_4', portfolioPhoto4Def);

  const introHtml = useContentToken('careerscontent.intro');
  const introText =
    plainText(introHtml) ||
    "Whether you're beginning your journey in property management or bringing years of experience, you'll find meaningful work, collaborative teammates, and leadership that believes in investing in people.";

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const jobsHref = jobsCta.url || '/contact-us';
  const jobsTarget = jobsCta._target || '_self';
  const jobsLabel = jobsCta.label || 'Explore Open Positions';

  const benefitTitleClass = 'font-heading text-xl font-bold leading-7 text-[rgb(var(--ala-text))]';
  const benefitBodyClass = 'font-heading text-base font-normal leading-6 text-[rgb(var(--ala-text))]';
  const faqQClass =
    'font-heading text-[clamp(18px,1.8vw,24px)] font-semibold text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]';
  const faqAClass = 'pb-6 font-heading text-base font-normal leading-6 text-[rgb(var(--ala-text))]/80';

  return (
    <>
      {/* Intro with scroll-highlight words */}
      <section className="relative flex w-full flex-col items-center overflow-clip pb-0">
        <span className="sr-only">
          <RichTextContent path="careerscontent.intro" label="Intro" as="span" />
        </span>
        <ScrollHighlightIntro text={introText} />
      </section>

      <AwardsMarquee />

      {/* Culture collage */}
      <section className="flex w-full flex-col items-center px-6 py-24 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          className="mb-16 flex flex-col items-center text-center"
        >
          <RichTextContent
            path="careerscontent.culture_headline"
            label="Culture Headline"
            as="h2"
            className="max-w-[800px] font-heading text-[clamp(2rem,6.67vw,96px)] font-extrabold lowercase leading-[0.95] tracking-[-0.03em] text-[rgb(var(--ala-text))]"
          />
          <RichTextContent
            path="careerscontent.culture_subhead"
            label="Culture Subhead"
            as="p"
            className="mt-4 font-heading text-[clamp(28px,3.47vw,50px)] font-normal italic leading-[1.2] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          />
          <svg
            viewBox="0 0 629 56"
            fill="none"
            className="mt-4 h-auto w-[629px] max-w-full overflow-visible"
            aria-hidden
          >
            <motion.path
              d="M4 34 C 70 10, 130 52, 210 26 S 330 4, 420 38 S 560 14, 625 30"
              className="stroke-[rgb(var(--ala-accent))]"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={alaViewport}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
            />
          </svg>
        </motion.div>

        <div className="relative mb-24 grid w-full max-w-[1360px] grid-cols-2 gap-3 md:aspect-[1360/664] md:block md:gap-0">
          <EditableSlotGroup groupPath="careerscontent.trademark" definition={trademarkDef}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={alaViewport}
              className="relative col-span-2 mx-auto aspect-square w-[50%] max-w-[200px] md:absolute md:left-[3.9%] md:top-[2.1%] md:z-0 md:col-auto md:mx-0 md:w-[31.3%] md:max-w-none"
            >
              <VibesitesImage
                url={
                  trademark.image ||
                  ''
                }
                alt={trademark.alt || ''}
                sizes="30vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="careerscontent.culture_photo_1" definition={culturePhoto1Def}>
            <motion.div
              initial={{ y: 50, opacity: 0, rotate: -10 }}
              whileInView={{ y: 0, opacity: 1, rotate: -6 }}
              viewport={alaViewport}
              transition={{ delay: 0.1 }}
              className="relative aspect-[296/351] overflow-clip rounded-xl shadow-xl md:absolute md:left-[17.5%] md:top-[24.7%] md:z-10 md:w-[21.7%]"
            >
              <VibesitesImage
                url={culture1.image || ''}
                alt={culture1.alt || ''}
                sizes="22vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="careerscontent.culture_photo_2" definition={culturePhoto2Def}>
            <motion.div
              initial={{ y: 50, opacity: 0, rotate: 10 }}
              whileInView={{ y: 0, opacity: 1, rotate: 4 }}
              viewport={alaViewport}
              transition={{ delay: 0.2 }}
              className="relative aspect-[275/335] overflow-clip rounded-xl shadow-xl md:absolute md:left-[31.7%] md:top-[33.1%] md:z-20 md:w-[20.2%]"
            >
              <VibesitesImage
                url={culture2.image || ''}
                alt={culture2.alt || ''}
                sizes="20vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="careerscontent.culture_photo_3" definition={culturePhoto3Def}>
            <motion.div
              initial={{ y: 50, opacity: 0, rotate: -5 }}
              whileInView={{ y: 0, opacity: 1, rotate: -2 }}
              viewport={alaViewport}
              transition={{ delay: 0.3 }}
              className="relative aspect-[283/341] overflow-clip rounded-xl shadow-xl md:absolute md:left-[47.5%] md:top-[24.7%] md:z-30 md:w-[20.8%]"
            >
              <VibesitesImage
                url={culture3.image || ''}
                alt={culture3.alt || ''}
                sizes="21vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="careerscontent.culture_photo_4" definition={culturePhoto4Def}>
            <motion.div
              initial={{ y: 50, opacity: 0, rotate: 8 }}
              whileInView={{ y: 0, opacity: 1, rotate: 6 }}
              viewport={alaViewport}
              transition={{ delay: 0.4 }}
              className="relative aspect-[275/335] overflow-clip rounded-xl shadow-xl md:absolute md:left-[63.6%] md:top-[28.9%] md:z-40 md:w-[20.2%]"
            >
              <VibesitesImage
                url={culture4.image || ''}
                alt={culture4.alt || ''}
                sizes="20vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>

          <div className="col-span-2 flex flex-wrap justify-center gap-2 pt-2 md:contents md:pt-0">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={alaViewport}
              transition={{ delay: 0.5, type: 'spring' }}
              className="whitespace-nowrap rounded-full bg-[rgb(var(--ala-secondary))] px-4 py-2 text-sm font-medium text-[rgb(var(--ala-on-secondary))] shadow-lg md:absolute md:left-[26.2%] md:top-[26.3%] md:z-50"
            >
              <RichTextContent
                path="careerscontent.culture_chip_1"
                label="Culture Chip 1"
                as="span"
              />
            </motion.div>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={alaViewport}
              transition={{ delay: 0.6, type: 'spring' }}
              className="whitespace-nowrap rounded-full bg-[rgb(var(--ala-background))] px-4 py-2 text-sm font-medium text-[rgb(var(--ala-text))] shadow-lg md:absolute md:left-[39.7%] md:top-[56.3%] md:z-50"
            >
              <RichTextContent
                path="careerscontent.culture_chip_2"
                label="Culture Chip 2"
                as="span"
              />
            </motion.div>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={alaViewport}
              transition={{ delay: 0.7, type: 'spring' }}
              className="whitespace-nowrap rounded-full bg-[rgb(var(--ala-accent))] px-4 py-2 text-sm font-medium text-[rgb(var(--ala-on-primary))] shadow-lg md:absolute md:left-[73.9%] md:top-[35.4%] md:z-50"
            >
              <RichTextContent
                path="careerscontent.culture_chip_3"
                label="Culture Chip 3"
                as="span"
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          className="flex max-w-[786px] flex-col items-center text-center"
        >
          <RichTextContent
            path="careerscontent.culture_body"
            label="Culture Body"
            as="p"
            className="mb-12 font-body text-xl font-normal leading-7 text-[rgb(var(--ala-text))]"
          />
          <div className="flex flex-col gap-6 sm:flex-row">
            <EditableSlotGroup groupPath="careerscontent.jobs_cta" definition={jobsCtaDef}>
              <a href={jobsHref} target={jobsTarget} className={primaryCtaClass}>
                <span>{jobsLabel}</span>
                <CtaArrow light />
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="careerscontent.culture_cta" definition={cultureCtaDef}>
              <a
                href={cultureCta.url || '/culture'}
                target={cultureCta._target || '_self'}
                className={secondaryCtaClass}
              >
                <span>{cultureCta.label || 'Learn About Our Culture'}</span>
                <CtaArrow />
              </a>
            </EditableSlotGroup>
          </div>
        </motion.div>
      </section>

      <GalleryMarquee />

      {/* Benefits */}
      <section className="mx-auto w-full max-w-[1200px] px-6 py-24 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          className="mb-16"
        >
          <RichTextContent
            path="careerscontent.benefits_headline"
            label="Benefits Headline"
            as="h2"
            className="font-heading text-[clamp(57px,7.22vw,104px)] font-bold leading-[0.95] text-[rgb(var(--ala-text))]"
          />
        </motion.div>

        <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_1_title" label="Benefit 1 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_1_body" label="Benefit 1 Body" as="p" className={benefitBodyClass} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.1 }}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_2_title" label="Benefit 2 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_2_body" label="Benefit 2 Body" as="p" className={benefitBodyClass} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.2 }}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_3_title" label="Benefit 3 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_3_body" label="Benefit 3 Body" as="p" className={benefitBodyClass} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_4_title" label="Benefit 4 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_4_body" label="Benefit 4 Body" as="p" className={benefitBodyClass} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.4 }}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_5_title" label="Benefit 5 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_5_body" label="Benefit 5 Body" as="p" className={benefitBodyClass} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.5 }}
            className="flex flex-col gap-3"
          >
            <RichTextContent path="careerscontent.benefit_6_title" label="Benefit 6 Title" as="h3" className={benefitTitleClass} />
            <RichTextContent path="careerscontent.benefit_6_body" label="Benefit 6 Body" as="p" className={benefitBodyClass} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          className="mt-16"
        >
          <a href={jobsHref} target={jobsTarget} className={primaryCtaClass}>
            <span>{jobsLabel}</span>
            <CtaArrow light />
          </a>
        </motion.div>
      </section>

      <QuotesCarousel />

      {/* Internship */}
      <section className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-16 px-6 py-24 md:px-12 lg:flex-row lg:gap-24">
        <div className="relative aspect-square w-full max-w-[600px] shrink-0">
          <EditableSlotGroup
            groupPath="careerscontent.internship_photo_1"
            definition={internshipPhoto1Def}
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={alaViewport}
              className="absolute left-0 top-0 z-10 aspect-[329/368] w-[60%] overflow-clip shadow-2xl"
            >
              <VibesitesImage
                url={internship1.image || ''}
                alt={internship1.alt || ''}
                sizes="40vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="careerscontent.internship_photo_2"
            definition={internshipPhoto2Def}
          >
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={alaViewport}
              transition={{ delay: 0.2 }}
              className="absolute bottom-0 right-0 z-20 aspect-[449/431] w-[75%] overflow-clip shadow-xl"
            >
              <VibesitesImage
                url={internship2.image || ''}
                alt={internship2.alt || ''}
                sizes="45vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </EditableSlotGroup>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          className="flex max-w-[600px] flex-col gap-8"
        >
          <RichTextContent
            path="careerscontent.internship_headline"
            label="Internship Headline"
            as="h2"
            className="font-heading text-[clamp(33px,4.17vw,60px)] font-semibold leading-[1.1] tracking-[-0.01em] text-[rgb(var(--ala-dark))]"
          />
          <RichTextContent
            path="careerscontent.internship_body"
            label="Internship Body"
            as="div"
            className="font-heading text-base font-normal leading-[1.8] text-[rgb(var(--ala-text))]/80 [&_p]:mb-4 [&_p:last-child]:mb-0"
          />
          <EditableSlotGroup groupPath="careerscontent.internship_cta" definition={internshipCtaDef}>
            <a
              href={internshipCta.url || '/contact-us'}
              target={internshipCta._target || '_self'}
              className={primaryCtaClass}
            >
              <span>{internshipCta.label || 'Internship Opportunities'}</span>
              <CtaArrow light />
            </a>
          </EditableSlotGroup>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="w-full bg-[rgb(var(--ala-surface))] px-6 py-24 md:px-12">
        <div className="mx-auto max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="mb-12 flex flex-col gap-3 text-center"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-secondary))]" aria-hidden />
              <RichTextContent
                path="careerscontent.faq_eyebrow"
                label="FAQ Eyebrow"
                as="span"
                className="font-heading font-medium uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-text))]"
              />
            </div>
            <RichTextContent
              path="careerscontent.faq_headline"
              label="FAQ Headline"
              as="h2"
              className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.05] tracking-[-0.02em] text-[rgb(var(--ala-text))]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.1 }}
            className="w-full"
          >
                <div className="border-b border-[rgb(var(--ala-text))]/15">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                    aria-expanded={openFaq === 0}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <RichTextContent path="careerscontent.faq_1_q" label="FAQ 1 Question" as="span" className={faqQClass} />
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                        openFaq === 0 ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === 0 ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <RichTextContent path="careerscontent.faq_1_a" label="FAQ 1 Answer" as="p" className={faqAClass} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
                <div className="border-b border-[rgb(var(--ala-text))]/15">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                    aria-expanded={openFaq === 1}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <RichTextContent path="careerscontent.faq_2_q" label="FAQ 2 Question" as="span" className={faqQClass} />
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                        openFaq === 1 ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === 1 ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <RichTextContent path="careerscontent.faq_2_a" label="FAQ 2 Answer" as="p" className={faqAClass} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
                <div className="border-b border-[rgb(var(--ala-text))]/15">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                    aria-expanded={openFaq === 2}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <RichTextContent path="careerscontent.faq_3_q" label="FAQ 3 Question" as="span" className={faqQClass} />
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                        openFaq === 2 ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === 2 ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <RichTextContent path="careerscontent.faq_3_a" label="FAQ 3 Answer" as="p" className={faqAClass} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
                <div className="border-b border-[rgb(var(--ala-text))]/15">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                    aria-expanded={openFaq === 3}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <RichTextContent path="careerscontent.faq_4_q" label="FAQ 4 Question" as="span" className={faqQClass} />
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                        openFaq === 3 ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === 3 ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <RichTextContent path="careerscontent.faq_4_a" label="FAQ 4 Answer" as="p" className={faqAClass} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
                <div className="border-b border-[rgb(var(--ala-text))]/15">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
                    aria-expanded={openFaq === 4}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <RichTextContent path="careerscontent.faq_5_q" label="FAQ 5 Question" as="span" className={faqQClass} />
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                        openFaq === 4 ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === 4 ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <RichTextContent path="careerscontent.faq_5_a" label="FAQ 5 Answer" as="p" className={faqAClass} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
          </motion.div>
        </div>
      </section>

      {/* Portfolio callout */}
      <section className="relative flex min-h-[100svh] w-full flex-col overflow-hidden bg-[rgb(var(--ala-dark))] px-6 py-10 text-[rgb(var(--ala-on-primary))] md:px-12">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, rgba(84,158,255,0.10), transparent 55%)',
          }}
          aria-hidden
        />
        <div className="relative z-10 grid flex-1 grid-cols-1 items-center gap-8 py-10 lg:grid-cols-2 lg:gap-6">
          <div className="order-2 flex flex-col justify-center lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <RichTextContent
                path="careerscontent.portfolio_headline"
                label="Portfolio Headline"
                as="h2"
                className="font-heading text-[clamp(40px,6.4vw,104px)] font-semibold leading-[0.98] tracking-[-0.035em] text-[rgb(var(--ala-on-primary))]"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ delay: 0.35, duration: 0.7 }}
            >
              <EditableSlotGroup groupPath="careerscontent.portfolio_cta" definition={portfolioCtaDef}>
                <a
                  href={portfolioCta.url || '/our-communities'}
                  target={portfolioCta._target || '_self'}
                  className="group mt-8 inline-flex w-fit items-center gap-3 font-heading text-base uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))] transition-colors hover:text-[rgb(var(--ala-on-primary))]"
                >
                  {portfolioCta.label || 'Our Portfolio'}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </EditableSlotGroup>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={alaViewport}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <RichTextContent
                path="careerscontent.portfolio_body"
                label="Portfolio Body"
                as="p"
                className="mt-10 max-w-md font-heading text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/60 md:text-lg"
              />
            </motion.div>
          </div>

          <div className="relative order-1 h-[58vh] w-full lg:order-2 lg:h-[78vh]">
            <EditableSlotGroup groupPath="careerscontent.portfolio_photo_1" definition={portfolioPhoto1Def}>
              <PortfolioCard index={0} image={portfolio1.image || ''} alt={portfolio1.alt || ''} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="careerscontent.portfolio_photo_2" definition={portfolioPhoto2Def}>
              <PortfolioCard index={1} image={portfolio2.image || ''} alt={portfolio2.alt || ''} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="careerscontent.portfolio_photo_3" definition={portfolioPhoto3Def}>
              <PortfolioCard index={2} image={portfolio3.image || ''} alt={portfolio3.alt || ''} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="careerscontent.portfolio_photo_4" definition={portfolioPhoto4Def}>
              <PortfolioCard index={3} image={portfolio4.image || ''} alt={portfolio4.alt || ''} />
            </EditableSlotGroup>
          </div>
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CareersContent',
  displayName: 'Careers Content',
  tokens: {
    'careerscontent.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        "<p>Whether you're beginning your journey in property management or bringing years of experience, you'll find meaningful work, collaborative teammates, and leadership that believes in investing in people.</p>",
    },
    'careerscontent.culture_headline': {
      label: 'Culture Headline',
      type: 'richtext',
      defaultValue: '<p>Where People Love to Work</p>',
    },
    'careerscontent.culture_subhead': {
      label: 'Culture Subhead',
      type: 'richtext',
      defaultValue: '<p>Multiple States Across the Southeast</p>',
    },
    'careerscontent.culture_chip_1': {
      label: 'Culture Chip 1',
      type: 'richtext',
      defaultValue: '<p>where careers take root</p>',
    },
    'careerscontent.culture_chip_2': {
      label: 'Culture Chip 2',
      type: 'richtext',
      defaultValue: '<p>excellence in every zip code</p>',
    },
    'careerscontent.culture_chip_3': {
      label: 'Culture Chip 3',
      type: 'richtext',
      defaultValue: '<p>great teams, no egos</p>',
    },
    'careerscontent.culture_body': {
      label: 'Culture Body',
      type: 'richtext',
      defaultValue:
        "<p>Our culture is built on teamwork, trust, and continuous improvement. We believe the best ideas come from people who feel valued and empowered to make an impact. Here, you'll work alongside passionate professionals who genuinely care about each other, our residents, and the communities we serve. We celebrate achievements, encourage innovation, and create opportunities for everyone to learn, lead, and grow.</p>",
    },
    'careerscontent.benefits_headline': {
      label: 'Benefits Headline',
      type: 'richtext',
      defaultValue: '<p>From us,<br/>for you.</p>',
    },
    'careerscontent.benefit_1_title': {
      label: 'Benefit 1 Title',
      type: 'richtext',
      defaultValue: '<p>Comprehensive Benefits</p>',
    },
    'careerscontent.benefit_1_body': {
      label: 'Benefit 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Your health and well-being come first. We offer competitive medical, dental, and vision coverage, retirement savings with company matching, and generous paid time off so you can focus on what matters most—both at work and at home.</p>',
    },
    'careerscontent.benefit_2_title': {
      label: 'Benefit 2 Title',
      type: 'richtext',
      defaultValue: '<p>Housing Discount</p>',
    },
    'careerscontent.benefit_2_body': {
      label: 'Benefit 2 Body',
      type: 'richtext',
      defaultValue:
        "<p>Enjoy a 20% discount on market rent at eligible American Landmark apartment communities. It's one of the many ways we invest in our associates and help make everyday living more affordable.</p>",
    },
    'careerscontent.benefit_3_title': {
      label: 'Benefit 3 Title',
      type: 'richtext',
      defaultValue: '<p>Career Growth</p>',
    },
    'careerscontent.benefit_3_body': {
      label: 'Benefit 3 Body',
      type: 'richtext',
      defaultValue:
        "<p>Whether you're joining as an intern, beginning your first role in multifamily, or advancing your leadership career, you'll have access to Landmark360, mentorship, professional development, and clear opportunities to grow within our organization.</p>",
    },
    'careerscontent.benefit_4_title': {
      label: 'Benefit 4 Title',
      type: 'richtext',
      defaultValue: '<p>Recognition</p>',
    },
    'careerscontent.benefit_4_body': {
      label: 'Benefit 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>We believe recognition is an important part of building a great workplace. From celebrating milestones to recognizing outstanding performance, we take time to acknowledge the people who make a difference every day.</p>',
    },
    'careerscontent.benefit_5_title': {
      label: 'Benefit 5 Title',
      type: 'richtext',
      defaultValue: '<p>Work-Life Balance</p>',
    },
    'careerscontent.benefit_5_body': {
      label: 'Benefit 5 Body',
      type: 'richtext',
      defaultValue:
        '<p>We believe a fulfilling career includes time to recharge. With paid time off, wellness-focused benefits, and a supportive team culture, we encourage our associates to maintain a healthy balance between work and life.</p>',
    },
    'careerscontent.benefit_6_title': {
      label: 'Benefit 6 Title',
      type: 'richtext',
      defaultValue: '<p>Exclusive Associate Perks</p>',
    },
    'careerscontent.benefit_6_body': {
      label: 'Benefit 6 Body',
      type: 'richtext',
      defaultValue:
        "<p>Take advantage of exclusive discounts on travel, entertainment, shopping, technology, and more through our associate perks program. It's our way of saying thank you for being part of the American Landmark team.</p>",
    },
    'careerscontent.quotes_eyebrow': {
      label: 'Quotes Eyebrow',
      type: 'richtext',
      defaultValue: '<p>our people</p>',
    },
    'careerscontent.quotes_headline': {
      label: 'Quotes Headline',
      type: 'richtext',
      defaultValue: '<p>Hear from our team members</p>',
    },
    'careerscontent.quote_1': {
      label: 'Quote 1',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark genuinely invests in its people. Through mentorship and real hands-on responsibility, I grew from an onsite role into regional leadership faster than I imagined possible.</p>',
    },
    'careerscontent.quote_2': {
      label: 'Quote 2',
      type: 'richtext',
      defaultValue:
        '<p>The culture here is collaborative, not competitive. Teams across markets share what works, and leadership actually listens. It makes every community we touch better.</p>',
    },
    'careerscontent.quote_3': {
      label: 'Quote 3',
      type: 'richtext',
      defaultValue:
        '<p>I started in the internship program with no multifamily experience. Within two years I was running my own community. The pathway here is real if you put in the work.</p>',
    },
    'careerscontent.internship_headline': {
      label: 'Internship Headline',
      type: 'richtext',
      defaultValue: '<p>Paid Property Management Internship Program</p>',
    },
    'careerscontent.internship_body': {
      label: 'Internship Body',
      type: 'richtext',
      defaultValue:
        "<p>Our culture is built on teamwork, trust, and continuous improvement. We believe the best ideas come from people who feel valued and empowered to make an impact. Here, you'll work alongside passionate professionals who genuinely care about each other, our residents, and the communities we serve.</p><p>Whether you're launching your career through our internship program or taking the next step as an experienced professional, you'll find opportunities to learn, grow, and make a meaningful impact. Through hands-on experience, mentorship, and ongoing development, we invest in helping every associate build a rewarding career and become a future leader in the multifamily industry.</p>",
    },
    'careerscontent.faq_eyebrow': {
      label: 'FAQ Eyebrow',
      type: 'richtext',
      defaultValue: '<p>CAREERS FAQ</p>',
    },
    'careerscontent.faq_headline': {
      label: 'FAQ Headline',
      type: 'richtext',
      defaultValue: '<p>Questions, Answered.</p>',
    },
    'careerscontent.faq_1_q': {
      label: 'FAQ 1 Question',
      type: 'richtext',
      defaultValue: '<p>How do I apply for a role at American Landmark?</p>',
    },
    'careerscontent.faq_1_a': {
      label: 'FAQ 1 Answer',
      type: 'richtext',
      defaultValue:
        '<p>You can browse all open positions on our careers page and apply directly online. Each listing includes the role description, required qualifications, and location. After submitting your application, our recruiting team reviews it and reaches out to qualified candidates to schedule an interview.</p>',
    },
    'careerscontent.faq_2_q': {
      label: 'FAQ 2 Question',
      type: 'richtext',
      defaultValue: '<p>What benefits does American Landmark offer?</p>',
    },
    'careerscontent.faq_2_a': {
      label: 'FAQ 2 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Full-time associates receive comprehensive medical, dental, and vision coverage, a 401(k) retirement plan with company matching, generous paid time off, a 20% housing discount at eligible American Landmark communities, and exclusive associate perks including discounts on travel, entertainment, and technology.</p>',
    },
    'careerscontent.faq_3_q': {
      label: 'FAQ 3 Question',
      type: 'richtext',
      defaultValue: '<p>Are remote or hybrid roles available?</p>',
    },
    'careerscontent.faq_3_a': {
      label: 'FAQ 3 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Many of our on-site roles, such as property management and maintenance, are location-based at our communities. Select corporate and corporate-support positions offer hybrid or remote flexibility. Each job listing specifies whether the role is on-site, hybrid, or remote.</p>',
    },
    'careerscontent.faq_4_q': {
      label: 'FAQ 4 Question',
      type: 'richtext',
      defaultValue: '<p>Is the internship program paid?</p>',
    },
    'careerscontent.faq_4_a': {
      label: 'FAQ 4 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Yes. Our Property Management Internship Program is a paid opportunity designed for students and early-career professionals. Interns gain hands-on experience across property management operations, mentorship from experienced leaders, and a clear pathway into full-time roles within the multifamily industry.</p>',
    },
    'careerscontent.faq_5_q': {
      label: 'FAQ 5 Question',
      type: 'richtext',
      defaultValue: '<p>Is American Landmark an equal opportunity employer?</p>',
    },
    'careerscontent.faq_5_a': {
      label: 'FAQ 5 Answer',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark is proud to be an Equal Opportunity Employer. We are committed to creating a diverse, inclusive workplace and provide equal employment opportunities to all associates and applicants without regard to race, color, religion, sex, national origin, age, disability, veteran status, or any other protected characteristic.</p>',
    },
    'careerscontent.portfolio_headline': {
      label: 'Portfolio Headline',
      type: 'richtext',
      defaultValue: '<p>We invest in the future of living.</p>',
    },
    'careerscontent.portfolio_body': {
      label: 'Portfolio Body',
      type: 'richtext',
      defaultValue:
        '<p>We back communities built to stand out — creating platforms whose impact extends across industries and generations.</p>',
    },
  },
  slotGroups: {
    'careerscontent.jobs_cta': jobsCtaDef,
    'careerscontent.culture_cta': cultureCtaDef,
    'careerscontent.internship_cta': internshipCtaDef,
    'careerscontent.portfolio_cta': portfolioCtaDef,
    'careerscontent.trademark': trademarkDef,
    'careerscontent.culture_photo_1': culturePhoto1Def,
    'careerscontent.culture_photo_2': culturePhoto2Def,
    'careerscontent.culture_photo_3': culturePhoto3Def,
    'careerscontent.culture_photo_4': culturePhoto4Def,
    'careerscontent.internship_photo_1': internshipPhoto1Def,
    'careerscontent.internship_photo_2': internshipPhoto2Def,
    'careerscontent.portfolio_photo_1': portfolioPhoto1Def,
    'careerscontent.portfolio_photo_2': portfolioPhoto2Def,
    'careerscontent.portfolio_photo_3': portfolioPhoto3Def,
    'careerscontent.portfolio_photo_4': portfolioPhoto4Def,
    'careerscontent.award_1': award1Def,
    'careerscontent.award_2': award2Def,
    'careerscontent.award_3': award3Def,
    'careerscontent.award_4': award4Def,
    'careerscontent.award_5': award5Def,
    'careerscontent.gallery_photo_1': galleryPhoto1Def,
    'careerscontent.gallery_photo_2': galleryPhoto2Def,
    'careerscontent.gallery_photo_3': galleryPhoto3Def,
    'careerscontent.gallery_photo_4': galleryPhoto4Def,
    'careerscontent.gallery_photo_5': galleryPhoto5Def,
    'careerscontent.gallery_photo_6': galleryPhoto6Def,
    'careerscontent.gallery_photo_7': galleryPhoto7Def,
    'careerscontent.gallery_photo_8': galleryPhoto8Def,
    'careerscontent.gallery_photo_9': galleryPhoto9Def,
    'careerscontent.gallery_photo_10': galleryPhoto10Def,
  },
};

(CareersContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default CareersContent;
